import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, Mail, MessageSquare, Phone, Users } from "lucide-react";

import { ApiError } from "../api/client";
import { Button, cx } from "./ui";
import type { ActivityType, ActivityWrite } from "../api/types";

export const ACTIVITY_KINDS = [
  { value: "note", label: "Note", icon: MessageSquare },
  { value: "call", label: "Call", icon: Phone },
  { value: "email", label: "Email", icon: Mail },
  { value: "whatsapp", label: "WhatsApp", icon: MessageSquare },
  { value: "sms", label: "SMS", icon: MessageSquare },
  { value: "meeting", label: "Meeting", icon: Users },
  { value: "task", label: "Task", icon: CheckCircle2 },
] as const;

const OUTCOMES = ["connected", "voicemail", "no_answer", "busy"] as const;

export function ActivityForm({
  onSave,
  onCancel,
}: {
  /** Saves and invalidates whatever the activity was logged against. */
  onSave: (body: ActivityWrite) => Promise<unknown>;
  onCancel: () => void;
}) {
  const [type, setType] = useState<ActivityType>("note");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [direction, setDirection] = useState("outbound");
  const [outcome, setOutcome] = useState("");
  const [dueAt, setDueAt] = useState("");

  const create = useMutation({
    mutationFn: () =>
      onSave({
        type,
        subject: subject || undefined,
        body: body || undefined,
        // Only sent when they mean something, because the API rejects a
        // direction on anything that is not a call.
        direction: type === "call" ? direction : null,
        outcome: type === "call" && outcome ? outcome : null,
        due_at: type === "task" && dueAt ? new Date(dueAt).toISOString() : null,
      }),
    onSuccess: () => {
      setSubject("");
      setBody("");
      setOutcome("");
      setDueAt("");
    },
  });

  const error = create.error instanceof ApiError ? create.error : null;
  const fieldErrors = error?.fields ?? {};

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    create.mutate();
  }

  return (
    <form onSubmit={onSubmit} className="rounded-lg bg-slate-50 p-4">
      <div className="space-y-3">
        <fieldset>
          {/* A button group, not an input. `fieldset`/`legend` is the correct
              pairing here; a `label for="type"` pointed at an element that does
              not exist, which is worse than no label at all. */}
          <legend className="text-xs font-medium text-slate-600">Kind</legend>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {ACTIVITY_KINDS.map((kind) => (
              <button
                key={kind.value}
                type="button"
                onClick={() => setType(kind.value)}
                aria-pressed={type === kind.value}
                className={cx(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium transition",
                  type === kind.value
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50",
                )}
              >
                <kind.icon size={12} />
                {kind.label}
              </button>
            ))}
          </div>
        </fieldset>

        <div>
          <label htmlFor="subject" className="block text-xs font-medium text-slate-600">
            Subject
          </label>
          <input
            id="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={type === "task" ? "What needs doing" : "One line about it"}
            className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
          {fieldErrors.subject ? (
            <p className="mt-1 text-xs text-rose-600">{fieldErrors.subject.join(" ")}</p>
          ) : null}
        </div>

        {type === "call" ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="direction" className="block text-xs font-medium text-slate-600">
                Direction
              </label>
              <select
                id="direction"
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
                className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
              >
                <option value="outbound">Outbound</option>
                <option value="inbound">Inbound</option>
              </select>
            </div>
            <div>
              <label htmlFor="outcome" className="block text-xs font-medium text-slate-600">
                Outcome
              </label>
              <select
                id="outcome"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
              >
                <option value="">Not set</option>
                {OUTCOMES.map((value) => (
                  <option key={value} value={value} className="capitalize">
                    {value.replace("_", " ")}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : null}

        {type === "task" ? (
          <div>
            <label htmlFor="due_at" className="block text-xs font-medium text-slate-600">
              Due
            </label>
            <input
              id="due_at"
              type="date"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
            />
            {fieldErrors.due_at ? (
              <p className="mt-1 text-xs text-rose-600">{fieldErrors.due_at.join(" ")}</p>
            ) : null}
          </div>
        ) : null}

        <div>
          <label htmlFor="body" className="block text-xs font-medium text-slate-600">
            Detail
          </label>
          <textarea
            id="body"
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending ? "Saving…" : "Save activity"}
          </Button>
          <Button onClick={onCancel} disabled={create.isPending}>
            Discard
          </Button>
        </div>
      </div>
    </form>
  );
}
